import React from "react";
import { Box, Typography } from "@mui/material";
import GridViewOutlinedIcon from "@mui/icons-material/GridViewOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { useTranslation } from "react-i18next";

/**
 * StatsPanel - Displays stats (categories, items, pages) and AI summary
 *
 * @param {Object} stats - { categories, items, pages }
 * @param {string} aiSummary - AI-generated summary text
 */
const StatsPanel = ({ stats, aiSummary }) => {
  const { t } = useTranslation("dashboard");

  const statItems = [
    {
      icon: <GridViewOutlinedIcon sx={{ fontSize: 16, color: "#7C3AED" }} />,
      value: stats.categories,
      label: t("ocrStatsCategoriesLabel"),
    },
    {
      icon: <RestaurantMenuOutlinedIcon sx={{ fontSize: 16, color: "#7C3AED" }} />,
      value: stats.items,
      label: t("ocrStatsItemsLabel"),
    },
    ...(stats.pages
      ? [
          {
            icon: <ImageOutlinedIcon sx={{ fontSize: 16, color: "#7C3AED" }} />,
            value: stats.pages,
            label: t("ocrStatsPagesLabel"),
            color: "#7C3AED",
          },
        ]
      : []),
  ];

  return (
    <Box>
      {/* Stats grid */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 1,
          border: "1px solid #e5e7eb",
          borderRadius: 2.5,
          p: 1.5,
          mb: 2,
        }}
      >
        {statItems.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
            }}
          >
            {item.icon}
            <Typography
              sx={{
                fontSize: "0.8rem",
                fontWeight: 700,
                color: item.color || "#7C3AED",
              }}
            >
              {item.value}
            </Typography>
            <Typography
              sx={{
                fontSize: "0.75rem",
                color: "#6b7280",
                textTransform: "lowercase",
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* AI Summary */}
      {aiSummary && (
        <Box
          sx={{
            bgcolor: "#f9fafb",
            borderRadius: 2.5,
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.75 }}>
            <Typography sx={{ fontSize: "0.7rem" }}>✨</Typography>
            <Typography
              sx={{
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "#111827",
                textTransform: "lowercase",
              }}
            >
              {t("ocrStatsAiSummary")}
            </Typography>
          </Box>
          <Typography
            sx={{
              fontSize: "0.78rem",
              color: "#6b7280",
              textTransform: "lowercase",
              lineHeight: 1.6,
            }}
          >
            {aiSummary}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default StatsPanel;
